import { Play, Pause, FastForward, ShieldAlert, Clock, Radio, Rewind } from "lucide-react";
import { THREAT } from "@/lib/constants";

const SPEEDS = [1, 2, 5];
const DRILLS = [
  { k: "cat1", l: "C1" }, { k: "cat2", l: "C2" }, { k: "cat3", l: "C3" },
  { k: "cat4", l: "C4" }, { k: "cat5", l: "C5" },
];
const REPLAYS = [
  { k: "tomas2010", l: "Tomas '10" }, { k: "ivan2004", l: "Ivan '04" },
  { k: "elsa2021", l: "Elsa '21" }, { k: "beryl2024", l: "Beryl '24" },
];

function MiniChart({ hours, cursor, onScrub }) {
  if (!hours || !hours.length) return null;
  const W = 1000, H = 90;
  const maxW = Math.max(1.5, ...hours.map((h) => h.water_level_m));
  const maxI = Math.max(1, ...hours.map((h) => h.isolated_count));
  const n = hours.length;
  const xf = (i) => (i / (n - 1)) * W;
  const surgePts = hours.map((h, i) => `${xf(i)},${H - (h.water_level_m / maxW) * (H - 12) - 4}`).join(" ");
  const isoPts = hours.map((h, i) => `${xf(i)},${H - (h.isolated_count / maxI) * (H - 20) - 4}`).join(" ");
  return (
    <svg
      data-testid="forecast-metrics-chart"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-[34px] cursor-crosshair"
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onScrub(Math.round(((e.clientX - r.left) / r.width) * (n - 1)));
      }}
    >
      <defs>
        <linearGradient id="surgeFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#38BDF8" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${H} ${surgePts} ${W},${H}`} fill="url(#surgeFill)" />
      <polyline points={surgePts} fill="none" stroke="#38BDF8" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
      <polyline points={isoPts} fill="none" stroke="#FF3B30" strokeWidth="2" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
      <line x1={xf(cursor)} y1="0" x2={xf(cursor)} y2={H} stroke="#FCC109" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      <circle cx={xf(cursor)} cy="5" r="4" fill="#FCC109" />
    </svg>
  );
}

export default function ThreatBanner({
  threat, peak, hours, forecastHour, playing, onPlayPause, onScrub,
  speed, onSpeed, drill, onDrill, onReplay, onLive, source, current, countdown,
}) {
  const tm = THREAT[threat] || THREAT.CLEAR;
  const hr = hours && hours[forecastHour];
  const offset = hr ? `+${hr.hour}h` : "+0h";
  const iso = hr ? (/[zZ]|[+-]\d\d:\d\d$/.test(hr.t) ? hr.t : `${hr.t}Z`) : null;
  const when = iso ? `${new Date(iso).toLocaleString("en-US", { weekday: "short", hour: "2-digit", minute: "2-digit", timeZone: "America/Barbados" })} AST` : "";
  const isDrill = !!drill;
  const isReplay = REPLAYS.some((r) => r.k === drill);

  return (
    <div
      data-testid="threat-level-hero-banner"
      className="relative shrink-0 glass border-y border-border overflow-hidden transition-all duration-700"
      style={{ boxShadow: `inset 0 0 90px ${tm.glow}`, borderColor: `${tm.color}55` }}
    >
      <div className="flex flex-col lg:flex-row items-stretch gap-3 px-4 py-2.5">
        {/* Threat state */}
        <div className="flex items-center gap-3 lg:w-[290px] shrink-0">
          <div
            className={`w-11 h-11 rounded-md flex items-center justify-center shrink-0 ${threat === "SEVERE" || threat === "CRITICAL" ? "risk-critical" : ""}`}
            style={{ background: `${tm.color}22`, border: `1px solid ${tm.color}` }}
          >
            <ShieldAlert className="w-6 h-6" style={{ color: tm.color }} />
          </div>
          <div className="min-w-0">
            <div className="font-mono-t text-[9px] uppercase tracking-[0.25em] text-slate-500">Threat Level</div>
            <div className="font-heading font-black text-2xl lg:text-3xl uppercase leading-none tracking-wide" style={{ color: tm.color }}>
              {tm.label}
            </div>
            {countdown && (
              <div data-testid="threat-countdown" className="font-mono-t text-[11px] font-bold text-risk-critical mt-0.5">
                ◉ {countdown}
              </div>
            )}
            {peak && !countdown && (
              <div data-testid="peak-surge-callout" className="font-mono-t text-[10px] text-slate-400 mt-0.5">
                Peak water level <span className="text-cyan font-bold">{peak.max_water_level_m ?? peak.water_level_m}m</span> @ +{peak.hour}h · {peak.isolated_count} isolated
              </div>
            )}
          </div>
        </div>

        {/* Time machine */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-1">
            <button data-testid="play-pause-forecast-button" onClick={onPlayPause}
              className="w-8 h-8 rounded-md bg-cyan text-slate-950 flex items-center justify-center hover:bg-cyan/85 transition-all shrink-0">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button onClick={() => onScrub(0)} className="w-7 h-7 rounded-md glass border border-border flex items-center justify-center text-slate-400 hover:text-cyan shrink-0" title="Reset">
              <Rewind className="w-3.5 h-3.5" />
            </button>
            <div data-testid="forecast-hour-display" className="font-mono-t text-xs text-cyan font-bold w-[52px] shrink-0 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {offset}
            </div>
            <input
              data-testid="forecast-time-machine-scrubber" type="range" min="0"
              max={hours ? hours.length - 1 : 71} value={forecastHour}
              onChange={(e) => onScrub(Number(e.target.value))}
              className="flex-1 accent-cyan h-1 min-w-0"
            />
            <div className="hidden sm:flex items-center gap-1 shrink-0">
              {SPEEDS.map((s) => (
                <button key={s} onClick={() => onSpeed(s)}
                  className={`font-mono-t text-[10px] px-1.5 py-0.5 rounded ${speed === s ? "bg-cyan text-slate-950" : "text-slate-400 hover:text-cyan"}`}>
                  {s}x
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono-t text-[9px] text-slate-400 sm:truncate leading-tight">
              {iso && isReplay ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "America/Barbados" }) : ""} {when} {isReplay ? "· HISTORICAL REPLAY" : isDrill ? "· SIMULATED DRILL" : "· live 72h forecast"}
            </span>
            {current && (
              <span className="font-mono-t text-[9px] text-slate-400 shrink-0">
                water <span className="text-cyan">{current.water_level_m}m</span> · flooded <span className="text-sky-300">{current.flooded_count}</span> · isolated <span className="text-risk-critical">{current.isolated_count}</span>
              </span>
            )}
          </div>
        </div>

        {/* Drill selector */}
        <div className="flex flex-col justify-center gap-1 lg:w-[270px] shrink-0">
          <div className="font-mono-t text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Radio className="w-3 h-3" /> Scenario
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="forecast-live-button" onClick={onLive}
              className={`font-mono-t text-[10px] px-2 py-1 rounded border uppercase tracking-wide ${!isDrill ? "bg-risk-low/15 border-risk-low/60 text-risk-low" : "border-border text-slate-400 hover:text-slate-200"}`}>
              Live
            </button>
            {DRILLS.map((d) => (
              <button key={d.k} data-testid={`drill-${d.k}`} onClick={() => onDrill(d.k)}
                className={`font-mono-t text-[10px] px-1.5 py-1 rounded border uppercase ${drill === d.k ? "bg-risk-critical/20 border-risk-critical text-risk-critical" : "border-border text-slate-400 hover:text-slate-200"}`}>
                {d.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {REPLAYS.map((r) => (
              <button key={r.k} data-testid={`replay-${r.k}`} onClick={() => onReplay(r.k)}
                className={`font-mono-t text-[9px] px-1.5 py-0.5 rounded border uppercase tracking-wide transition-colors ${drill === r.k ? "bg-amber-400/20 border-amber-400 text-amber-300" : "border-border text-slate-500 hover:text-amber-300"}`}>
                {r.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 pb-1.5">
        <MiniChart hours={hours} cursor={forecastHour} onScrub={onScrub} />
      </div>
    </div>
  );
}
